import { action, makeAutoObservable, observable, runInAction } from "mobx";
import BlockBlobController, { PostBlockBlobResponseModel } from "../../src/controllers/BlockBlobController";
import { BlockJson, BlockJsonModel, BlockTypes, CategoryJsonModel, CategoryPackageJsonModel, LayoutJsonModel, LicenseJsonModel, ModeJsonModel } from "../models/BlockTransportSystemJsonModel";
import GlobalEnvironments from "../../src/constants/GlobalEnvironments";
import { SortingOption } from "../constants/SortingOption";
import StudioAdminController, { ApiResultModel, ExposureCategoryModel } from "../controllers/StudioAdminController";
import { PolarProductTypes } from "../constants/PolarProductTypes";
import { cropImageBlobAsync, resizeImageBlobAsync } from "../modules/ImageCanvas";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import { UploadBlockModel } from "../models/UploadBlockModel";
import { AdminUserModel } from "../models/AccountPermissionModel";
import i18n from "../../shared/internationalization/I18nSetting";

const all = "all";

const subPath = GlobalEnvironments.getBlockTransportSystemSubUrlForWeb();
const createPath = (fileName: string) => {
  return `${subPath}/${fileName}`;
};

export default class BlockKitStore {
  @observable public blocks: BlockJson[] = [];
  @observable public allBlocks: BlockJson[] = [];
  @observable public allCategories: CategoryJsonModel[] = [];
  @observable public deletedCategories: CategoryJsonModel[] = [];
  @observable public publishedCategories: CategoryJsonModel[] = [];
  @observable public allExposureCategories: ExposureCategoryModel[] = [];
  @observable public allCategoryPackages: CategoryPackageJsonModel[] = [];
  @observable public publishedCategoryPackages: CategoryPackageJsonModel[] = [];
  @observable public allLicenses: LicenseJsonModel[] = [];
  @observable public publishedLicenses: LicenseJsonModel[] = [];
  @observable public allModes: ModeJsonModel[] = [];
  @observable public publishedModes: ModeJsonModel[] = [];
  @observable public allUsers: AdminUserModel[] = [];
  @observable public layouts: LayoutJsonModel[] = [];
  @observable public publishedLayouts: LayoutJsonModel[] = [];
  @observable public textAlignTypesOptions: string[] = [all];
  @observable public layoutTypesOptions: string[] = [all];
  @observable public tagsOptions: string[] = [all];
  @observable public selectedCategoryId: string = all;
  @observable public selectedLicenseId: string = all;
  @observable public selectedType: string = all;
  @observable public selectedTextAlignType: string = all;
  @observable public selectedLayoutType: string = all;
  @observable public selectedTag: string = all;
  @observable public selectedSortingOption: SortingOption = SortingOption.new;
  @observable public selectedProductOption: string = all;
  @observable public searchWord: string = "";
  @observable timeZone: string = "Asia/Seoul";
  public supportedTimeZones: string[] = ["Asia/Seoul", "Asia/Tokyo", "Asia/Shanghai", "Asia/Singapore", "Asia/Bangkok", "Asia/Ulaanbaatar", "America/Vancouver" ]

  public blobController = new BlockBlobController(null);
  public studioController = new StudioAdminController(
    process.env.REACT_APP_STUDIO_API_HOST_URL,
    process.env.REACT_APP_STUDIO_API_KEY_ADMIN
  )

  constructor() {
    makeAutoObservable(this);
  }

  public async initAsync() {
    const blocks = await this.studioController.getBlocksStagingAsync();
    const categories = await this.studioController.getCategoriesStagingAsync();
    const licenses = await this.studioController.getLicensesStagingAsync();
    const layouts = await this.studioController.getLayoutsStagingAsync();
    runInAction(() => {
      this.allBlocks = blocks.map((data) => new BlockJson(data, GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()));
      this.blocks = [];
      this.allCategories = categories;
      this.allLicenses = licenses;
      this.layouts = layouts;
      this.initBlockOptions(blocks);
    });
  }

  public async initSearchedBlocksAsync() {
    const blocks = await this.studioController.getBlocksStagingAsync();
    runInAction(() => {
      this.allBlocks = blocks.map((data) => new BlockJson(data, GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()));
      this.refreshBlocksAsFilter();
    });
  }

  // 라이선스가 없는 디자인은 게시할 수 있고, 라이선스가 있으면 그 라이선스가 게시되어 있어야 한다.
  public async isPublishableLicenseAsync(licenseId?: string) {
    if (!licenseId) {
      return true;
    }

    const publishedLicenses = await this.studioController.getLicensesAsync();
    return publishedLicenses.some(license => license.id === licenseId);
  }

  public async initLayoutPageAsync() {
    const layouts = await this.studioController.getLayoutsStagingAsync();
    const publishedLayouts = await this.studioController.getLayoutsAsync();
    runInAction(() => {
      this.layouts = layouts;
      this.publishedLayouts = publishedLayouts;
    });
  }

  public async initLicensePageAsync() {
    const blocks = await this.studioController.getBlocksStagingAsync();
    const licenses = await this.studioController.getLicensesStagingAsync();
    const publishedLicenses = await this.studioController.getLicensesAsync();
    runInAction(() => {
      this.allBlocks = blocks.map((data) => new BlockJson(data, GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()));
      this.allLicenses = licenses;
      this.publishedLicenses = publishedLicenses;
      this.initBlockOptions(blocks);
    });
  }

  public async initCategoryPackagePageAsync() {
    const categories = await this.studioController.getCategoriesStagingAsync();
    const categoryPackages = await this.studioController.getCategoryPackagesStagingAsync();
    const publishedCategoryPackages = await this.studioController.getCategoryPackagesAsync();
    runInAction(() => {
      this.allCategories = categories;
      this.allCategoryPackages = categoryPackages;
      this.publishedCategoryPackages = publishedCategoryPackages;
    });
  }

  public async initCategoryPageAsync() {
    const blocks = await this.studioController.getBlocksStagingAsync();
    const categories = await this.studioController.getCategoriesStagingAsync();
    const publishedCategories = await this.studioController.getCategoriesAsync();
    const categoryPackages = await this.studioController.getCategoryPackagesStagingAsync();
    const exposureCategories = await this.studioController.getExposureCategoriesStagingAsync(null);
    runInAction(() => {
      this.allBlocks = blocks.map((data) => new BlockJson(data, GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()));
      this.allCategories = categories;
      this.publishedCategories = publishedCategories;
      this.allExposureCategories = exposureCategories;
      this.allCategoryPackages = categoryPackages;
      this.initBlockOptions(blocks);
    });
  }

  // 계정 권한 설정 페이지 — 유저 목록 + 스테이징 카탈로그(모드/카테고리/라이선스).
  // 계정 목록은 users만 필요 → 빠르게(≈0.4s) 따로 로드. (느린 staging 카탈로그와 분리)
  public async loadAccountUsersAsync() {
    const users = await this.studioController.getUsersAsync();
    runInAction(() => { this.allUsers = users; });
  }

  // 권한 부여 후보 카탈로그(모드/카테고리/라이선스). staging 응답이 느려(≈3.5s) 목록과 분리해 백그라운드 로드.
  public async loadAccountCatalogAsync() {
    // 서로 의존 없으니 동시 호출 (총 소요 ≈ 가장 느린 1개)
    // 모드 후보 = 스테이징 모드(노출 카테고리/라이선스 배열을 자체 포함). 카테고리/라이선스는 이름 해석·후보 풀용.
    const [modes, stagingCategories, deletedStaingCategories, stagingLicenses] = await Promise.all([
      this.studioController.getModesStagingAsync(),
      this.studioController.getCategoriesStagingAsync(),
      this.studioController.getCategoriesStagingAsync(true),
      this.studioController.getLicensesStagingAsync(),
    ]);
    runInAction(() => {
      this.allModes = modes;
      this.allCategories = stagingCategories;
      this.deletedCategories = deletedStaingCategories;
      this.allLicenses = stagingLicenses;
    });
  }

  // 계정 편집용 깊은 복사 (edited/original 분리)
  public copyAccount(account: AdminUserModel): AdminUserModel {
    return cloneDeep(account);
  }

  // 빈 계정 (선택 전 초기 edited용)
  public createAccount(): AdminUserModel {
    return { userId: "", email: "", name: "", role: "default", allowedLocales: ["ko-KR"], modes: [] };
  }

  public async initModePageAsync() {
    const categories = await this.studioController.getCategoriesStagingAsync();
    const licenses = await this.studioController.getLicensesStagingAsync();
    const modes = await this.studioController.getModesStagingAsync();
    const publishedModes = await this.studioController.getModesAsync();
    runInAction(() => {
      this.allCategories = categories;
      this.allLicenses = licenses;
      this.allModes = modes;
      this.publishedModes = publishedModes;
    });
  }

  private initBlockOptions(blocks: BlockJsonModel[]) {
      this.textAlignTypesOptions = Array.from(new Set(blocks.map(block => block.textAlignType)));
      this.layoutTypesOptions = Array.from(new Set(blocks.map(block => block.layoutType).filter(Boolean))).sort((a, b) => {
      if (a === BlockTypes.none) return -1;
      if (b === BlockTypes.none) return 1;
      return a.localeCompare(b);
    });
      this.tagsOptions = Array.from(
        new Set([
          "all",
          ...blocks
            .filter(block => block?.tags && block.tags.length > 0)
            .flatMap(block => block.tags)
        ])
      );
  }

  @action public setTimeZone(timeZone: string) {
    this.timeZone = timeZone;
  }

  public setCategoryId(categoryId: string){
    this.selectedCategoryId = categoryId;
  }

  public setLicenseId(licenseId: string) {
    this.selectedLicenseId = licenseId ?? "none";
  }

  public setType(type: string) {
    this.selectedType = type;
  }

  public setTextAlignType(textAlignType: string) {
    this.selectedTextAlignType = textAlignType;
  }

  public setLayoutType(layoutType: string) {
    this.selectedLayoutType = layoutType;
  }

  public setSortingOption(sortingOption: SortingOption) {
    this.selectedSortingOption = sortingOption;
  }

  public setProductOption(productOption: PolarProductTypes) {
    this.selectedProductOption = productOption;
  }

  public setTag(tag: string) {
    this.selectedTag = tag;
  }

  public setSearchWord(searchWord: string) {
    this.searchWord = searchWord;
  }

  private sortBlocks(sortingOption: SortingOption) {
    if (sortingOption === SortingOption.noSorting) {
      return this.allBlocks;
    }
    else if(sortingOption === SortingOption.nameAsc) {
      return this.allBlocks.sort((a, b) => (a.data.name > b.data.name) ? 1 : ((b.data.name > a.data.name) ? -1 : 0));
    }
    else if (sortingOption === SortingOption.nameDesc) {
      return this.allBlocks.sort((a, b) => (a.data.name > b.data.name) ? -1 : ((b.data.name > a.data.name) ? 1 : 0));
    }
    else if (sortingOption === SortingOption.recentlyModified) {
      return this.allBlocks.sort((a, b) => (a.data.updateTime > b.data.updateTime) ? -1 : ((b.data.updateTime > a.data.updateTime) ? 1 : 0));
    }
    else if (sortingOption === SortingOption.old) {
      return this.allBlocks.sort((a, b) => (a.data.createTime > b.data.createTime) ? 1 : ((b.data.createTime > a.data.createTime) ? -1 : 0));
    }
    else if (sortingOption === SortingOption.new) {
      return this.allBlocks.sort((a, b) => (a.data.createTime > b.data.createTime) ? -1 : ((b.data.createTime > a.data.createTime) ? 1 : 0));
    }
  }

  public refreshBlocksAsFilter() {
    const blocks = this.sortBlocks(this.selectedSortingOption as SortingOption);

    this.blocks = blocks.filter(val => {
      const isCategorySame = val.data?.categoryId === this.selectedCategoryId;
      const isLicenseSame = (val.data.licenseId ?? "none") === this.selectedLicenseId;
      const isTypeSame = val.data.type === this.selectedType;
      const isTextAlignSame = (val.data.textAlignType === null ? "null" : val.data.textAlignType) === this.selectedTextAlignType;
      const isLayoutTypeSame = (val.data.layoutType === null ? "none" : val.data.layoutType) === this.selectedLayoutType;
      const isTagSame = val.data.tags !== null && val.data.tags.length !== 0 && val.data.tags.indexOf(this.selectedTag) !== -1;
      const isSearchWordEmpty = this.searchWord === "";
      const isSearchWordInDescription = val.data.description !== undefined && val.data.description.indexOf(this.searchWord) !== -1;
      const isSearchWordInCategory = val.data.category.indexOf(this.searchWord) !== -1;
      const isSearchWordInName = val.data.name.indexOf(this.searchWord) !== -1;

      if (!isCategorySame && this.selectedCategoryId !== all) {
        return false;
      }

      if (!isLicenseSame && this.selectedLicenseId !== all) {
        return false;
      }

      if (!isTypeSame && this.selectedType !== all) {
        return false;
      }

      if (!isTextAlignSame && this.selectedTextAlignType !== all) {
        return false;
      }

      if (!isLayoutTypeSame && this.selectedLayoutType !== all) {
        return false;
      }

      if (!isTagSame && this.selectedTag !== all) {
        return false;
      }

      if (this.selectedProductOption !== all &&
        (this.selectedProductOption === "none"
          ? val.data.dedicatedProducts?.length !== 0
          : !val.data.dedicatedProducts || !val.data.dedicatedProducts.includes(this.selectedProductOption))
       ) {
        return false;
       }

      if(!isSearchWordEmpty && !isSearchWordInDescription && !isSearchWordInCategory && !isSearchWordInName ) {
        return false;
      }

      return true;
    })
  }

  public async publishBlocks(categoryId: string, blockType: string, progressEvent: (name: string, count: number, totalCount: number) => void): Promise<ApiResultModel> {
    const expiredLicense = this.allLicenses.filter(l => new Date(l.expireDate) < new Date()).map(l => l.id);
    const targets = this.allBlocks.filter(b => b.data.categoryId === categoryId && b.data.type === blockType &&
      (b.data.licenseId ? !expiredLicense.includes(b.data.licenseId) : true)
    );


    // 모달은 게시가 끝나면 닫히므로, 마지막 진행 문구를 결과로 돌려줘 호출부에서 알림으로 표시한다.
    const finish = (isSuccess: boolean, name: string, count: number, totalCount: number): ApiResultModel => {
      progressEvent(name, count, totalCount);
      return { isSuccess, message: `${name} : ${count}/${totalCount}` };
    };

    if (targets.length === 0) {
      return finish(false, i18n.t("publish-no-target"), 0, 0);
    }

    // 미게시 라이선스의 디자인은 서버에서 게시가 실패하므로, 게시를 시작하기 전에 중단한다.
    let publishedLicenseIds: string[];
    try {
      publishedLicenseIds = (await this.studioController.getLicensesAsync()).map(license => license.id);
    } catch (error) {
      return finish(false, `${i18n.t("publish-failed")}(${i18n.t("license-load-failed")})`, 0, targets.length);
    }

    const unpublishedLicenseBlocks = targets.filter(b => b.data.licenseId && !publishedLicenseIds.includes(b.data.licenseId));
    if (unpublishedLicenseBlocks.length > 0) {
      const names = unpublishedLicenseBlocks.slice(0, 5).map(b => b.data.name).join(", ");
      const more = unpublishedLicenseBlocks.length > 5 ? ` ${i18n.t("and-more-count", { count: unpublishedLicenseBlocks.length - 5 })}` : "";
      return finish(false, i18n.t("publish-stopped-unpublished-license", { names: `${names}${more}` }), 0, targets.length);
    }

    let count = 0;
    try {
      for (const block of targets) {
        progressEvent(`${i18n.t("publishing")}(${block.data.name})`, count, targets.length);
        const response = await this.studioController.publishBlockStagingAsync(block.data);
        // 첫 실패에서 중단한다. count는 중단 전까지 게시된 개수.
        if (!response.isSuccess) {
          return finish(false, `${i18n.t("publish-failed")}(${block.data.name} / ${response.message})`, count, targets.length);
        }
        count += 1;
      }
  
      return finish(true, i18n.t("publish-success"), count, targets.length);
    } catch (error) {
      return finish(false, `${i18n.t("publish-failed")}(${this.studioController.getErrorMessage(error).message})`, count, targets.length);
    }
  }

  public async addNewImageBlocks(
    blockList: UploadBlockModel[],
    progressCallBack?: (cnt: number, total: number) => void
  ) {
    // upload block을 업로드하기
    const resultBlocks = [];
    for (let index = 0; index < blockList.length; index++) {
      const block = blockList[index];
      const file = block.image;
      const time = new Date().toISOString();
      // 새로운 block 생성
      const newBlock: BlockJsonModel = {
        id: uuidv4(),
        category: block.category,
        categoryId: block.categoryId,
        license: block.license,
        licenseId: block.licenseId,
        tags: block.tags,
        textAlignType: block.textAlignType,
        layoutId: block.layoutId,
        layoutType: block.layoutType,
        type: block.type,
        name:
          block.name.length === 0
            ? `${block.category}_${index < 9 ? "0" + (index + 1) : index + 1}`
            : block.name,
        subjectName: block.subjectName,
        priority: block.priority,
        originUrl: null,
        previewUrl: null,
        iconUrl: null,
        description: block.description,
        createTime: time,
        updateTime: time,
        switchableBlockModel: block.switchableBlockModel,
        imageDirection: block.imageDirection,
        disabled: false,
        deleted: false,
        dedicatedProducts: block.dedicatedProducts,
        releaseDate: block.releaseDate,
        expireDate: block.expireDate,
        grade: block.grade,
        mediaSize: block.mediaSize
      };

      // icon, preview file 올리기
      const name = file.name;
      const extension = name.split(".").pop();
      const fileName = `${newBlock.id}-origin.${extension}`;
      const path = createPath(fileName);
      const result = await this.blobController.postAsync(file, path);
      newBlock.originUrl = result.path;

      if (block.previewImage) {
        newBlock.previewUrl = await this.uploadPreviewAsync(newBlock, block.previewImage, block.imageWidth / 2, block.imageHeight / 2);
      } else {
        newBlock.previewUrl = await this.getPreviewUrl(block.type as BlockTypes, result.path, file, newBlock.id, block.imageWidth, block.imageHeight);
      }

      if (block.iconImage) {
        newBlock.iconUrl = await this.uploadIconAsync(newBlock, block.iconImage, 160, 120);
      } else {
        newBlock.iconUrl = await this.getIconUrl(block.type as BlockTypes, result.path, file, newBlock.id);
      }

      // 커팅 이미지가 있는 경우, 추가한다.
      if (block.cutImage) {
        newBlock.cutUrl = await this.getCutUrl(newBlock.id, "svg", block.cutImage);
      }

      if (block.silverImage) {
        newBlock.silverUrl = await this.uploadSilverAsync(newBlock, block.silverImage);
      }

      if (block.backOriginImage) {
        newBlock.backOriginUrl = await this.uploadBackOriginAsync(newBlock, block.backOriginImage);
      }

      if (block.backSilverImage) {
        newBlock.backSilverUrl = await this.uploadBackSilverAsync(newBlock, block.backSilverImage);
      }

      resultBlocks.push(newBlock);
      if (progressCallBack) progressCallBack(index, blockList.length);
    }

    return resultBlocks;
  }

  public async createLicenseAsync(license: LicenseJsonModel, file?: File) {
    license.id = uuidv4();
    license.thumbnailUrl = "";
    return await this.updateLicenseAsync(license, file);
  }

  public async updateLicenseAsync(license: LicenseJsonModel, file?: File) {
    if (file) {
      const name = file.name;
      const extension = name.split(".").pop();
      const fileName = `${license.id}-thumbnail.${extension}`;
      const path = createPath(fileName);
  
      const result = await this.blobController.postAsync(file, path);
      license.thumbnailUrl = result.path;
    }

    return license;
  }

  public async addCategory(category: CategoryJsonModel, file?: File) {
    if (file) {
      const name = file.name;
      const extension = name.split(".").pop();
      const fileName = `${category.id}-thumbnail.${extension}`;
      const path = createPath(fileName);
  
      const result = await this.blobController.postAsync(file, path);
      category.thumbnailUrl = result.path;
    } else {
      category.thumbnailUrl = "";
    }
    
    return {
      ...category,
      id: uuidv4()
    };
  }

  public async copyCategory(category: CategoryJsonModel) {
    return {
      ...category,
      thumbnailUrl: "",
      id: uuidv4(),
      blockCount: 0
    }
  }

  public async changeCategoryAsync(category: CategoryJsonModel, file?: File) {
    if (file) {
      const name = file.name;
      const extension = name.split(".").pop();
      const fileName = `${category.id}-thumbnail.${extension}`;
      const path = createPath(fileName);
  
      const result = await this.blobController.postAsync(file, path);
      category.thumbnailUrl = result.path;
    }

    return category;
  }

  // 이미지들은 한번에 수정되므로 Init과 Publish 따로 합쳐서 실행
  public async uploadOriginAsync(block: BlockJsonModel, file: File) {
    const name = file.name;
    const extension = name.split(".").pop();
    const fileName = `${block.id}-origin.${extension}`;
    const path = createPath(fileName);

    const result = await this.blobController.postAsync(file, path);
    return result.path;
  }

  // 이미지들은 한번에 수정되므로 Init과 Publish 따로 합쳐서 실행
  public async uploadSilverAsync(block: BlockJsonModel, file: File) {
    const name = file.name;
    const extension = name.split(".").pop();
    const fileName = `${block.id}-silver.${extension}`;
    const path = createPath(fileName);

    const result = await this.blobController.postAsync(file, path);
    return result.path;
  }

  // 이미지들은 한번에 수정되므로 Init과 Publish 따로 합쳐서 실행
  public async uploadBackOriginAsync(block: BlockJsonModel, file: File) {
    const name = file.name;
    const extension = name.split(".").pop();
    const fileName = `${block.id}-back-origin.${extension}`;
    const path = createPath(fileName);
    
    const result = await this.blobController.postAsync(file, path);
    return result.path;
  }

  // 이미지들은 한번에 수정되므로 Init과 Publish 따로 합쳐서 실행
  public async uploadBackSilverAsync(block: BlockJsonModel, file: File) {
    const name = file.name;
    const extension = name.split(".").pop();
    const fileName = `${block.id}-back-silver.${extension}`;
    const path = createPath(fileName);

    const result = await this.blobController.postAsync(file, path);
    return result.path;
  }

  // 이미지들은 한번에 수정되므로 Init과 Publish 따로 합쳐서 실행
  public async uploadCutAsync(block: BlockJsonModel, file: File) {
    const name = file.name;
    const extension = name.split(".").pop();
    const fileName = `${block.id}-cut.${extension}`;
    const path = createPath(fileName);

    const result = await this.blobController.postAsync(file, path);
    return result.path
  }

  // 이미지들은 한번에 수정되므로 Init과 Publish 따로 합쳐서 실행
  public async uploadPreviewAsync(block: BlockJsonModel, file: File, width: number, height: number) {
    const convertedFile = await resizeImageBlobAsync(file, width, height, "image/webp", 0.8);
    const fileName = `${block.id}-preview.webp`;
    const path = createPath(fileName);

    const result = await this.blobController.postAsync(convertedFile, path);
    return result.path;
  }

  // 이미지들은 한번에 수정되므로 Init과 Publish 따로 합쳐서 실행
  public async uploadIconAsync(block: BlockJsonModel, file: File, width: number, height: number) {
    const convertedFile = await resizeImageBlobAsync(file, width, height, "image/webp", 0.8);
    const fileName = `${block.id}-icon.webp`;
    const path = createPath(fileName);

    const result = await this.blobController.postAsync(convertedFile, path);
    return result.path;
  }

  private async getPreviewUrl(type: string, originUrl: string, originFile: File, id: string, width: number, height: number) {
    if (type === BlockTypes.badge || type === BlockTypes.featuredPhoto) {
      return originUrl;
    }
    else {
      let previewResult: PostBlockBlobResponseModel;
      try {
        const previewFile = await resizeImageBlobAsync(originFile, width / 2, height / 2, "image/webp", 0.8);
        const previeName = `${id}-preivew.webp`;
        const previePath = createPath(previeName);
        previewResult = await this.blobController.postAsync(previewFile, previePath);
      } catch (error) {
        console.error(error);
      }
      return previewResult.path;
    }
  }

  private async getIconUrl(type: string, originUrl: string, originFile: File, id: string) {
    if (type === BlockTypes.badge || type === BlockTypes.featuredPhoto) {
      return originUrl;
    }
    else {
      let iconResult: PostBlockBlobResponseModel;
      try {
        const iconFile = await cropImageBlobAsync(originFile, (1844 - 742) / 2, (1240 - 588), 742, 588, 0, 0, 160, 120, "image/webp", 0.8);
        const iconName = `${id}-icon.webp`;
        const iconPath = createPath(iconName);
        iconResult = await this.blobController.postAsync(iconFile, iconPath);
      } catch (error) {
        console.error(error);
      }
      return iconResult.path;
    }
  }

  private async getCutUrl(id: string, extension: string, cutFile: File) {
    let iconResult: PostBlockBlobResponseModel;

    try {
      const cutName = `${id}-cut.${extension}`;
      const cutPath = createPath(cutName);
      iconResult = await this.blobController.postAsync(cutFile, cutPath);
    } catch (error) {
      console.error(error);
    }

    return iconResult.path;
  }
}
